import jwt_decode from 'jwt-decode';
import request, { Response, ResponseError } from 'superagent';
import { ConstraintViolationList, UserRole } from 'types';

const backendBaseUrl = process.env.REACT_APP_API_BASE_URL || '';

export interface AccessToken {
  exp: number;
  roles: UserRole[];
}
interface RequestOption {
  checkToken?: boolean;
  fullbody?: boolean;
}

function tokenHasExpired(token: AccessToken): boolean {
  if (!token.exp) return true;

  // Less than 10 seconds remaining => token has expired
  const now = new Date().getTime() / 1000;
  return token.exp - now < 10;
}

type Method = 'get' | 'post' | 'put' | 'patch' | 'delete';

class Client {
  baseUrl: string;
  withCredentials: boolean;
  agent: request.SuperAgentStatic & request.Request;
  tokenKey = 'token';

  constructor(baseUrl: string, withCredentials = true) {
    this.baseUrl = baseUrl;
    this.withCredentials = withCredentials;
    this.agent = request.agent();
    this.agent.accept('application/ld+json');
    if (withCredentials) {
      this.agent.withCredentials();
    }
  }

  async request(method: Method, endpoint: string, data: object | null = null, options: RequestOption = {}) {
    let token: string | null = '';
    if (this.withCredentials) {
      // Checking token validity, refreshing it if necessary.
      if (options.checkToken !== false) {
        token = await this.checkToken();
      } else {
        token = this.getToken();
      }

      if (!token && options.checkToken !== false && window.location.pathname !== '/') {
        window.location.pathname = '/';
      }
    }

    const url = /^https?:\/\//.test(endpoint) ? endpoint : `${this.baseUrl}${endpoint}`;
    let promise = this.agent[method](url);

    if (token && this.withCredentials) {
      promise = promise.set('Authorization', `Bearer ${token}`);
    }

    if (['post', 'put', 'patch'].includes(method) && data) {
      promise = promise.send(data);
    }

    const { body } = await promise;
    if (options.fullbody) {
      return body;
    }

    return body?.['hydra:member'] || body;
  }

  getToken() {
    return localStorage.getItem(this.tokenKey);
  }

  updateToken(token: string) {
    return localStorage.setItem(this.tokenKey, token);
  }

  getErrorResponse(error: ResponseError): Response | null {
    return error.response ?? null;
  }

  getErrorResponseBody(error: ResponseError): any {
    return this.getErrorResponse(error)?.body ?? null;
  }

  getErrorResponseBodyMessageFromApiPlatform(error: ResponseError): string | null {
    return this.getErrorResponseBody(error)?.['hydra:description'] ?? null;
  }

  getConstraintViolationListFromApiPlatform(error: ResponseError): ConstraintViolationList | null {
    return this.getErrorResponseBody(error) ?? null;
  }

  getErrorResponseStatus(error: ResponseError): number | null {
    return client.getErrorResponse(error)?.status ?? null;
  }

  /**
   * This function assess the access token is still valid, if not it refreshes it.
   * In case of error during the refresh process it disconnects the user and redirects to the login page.
   */
  async checkToken() {
    let token = this.getToken();

    if (!token || tokenHasExpired(jwt_decode<AccessToken>(token))) {
      try {
        token = await this.refreshToken();
      } catch (e) {
        // Token was invalid, logging out the user.
        token = '';
        this.updateToken('');
        this.logout();
      }
    }

    return token;
  }

  get(endpoint: string, options: RequestOption = {}) {
    return this.request('get', endpoint, null, options);
  }

  delete(endpoint: string, options: RequestOption = {}) {
    return this.request('delete', endpoint, null, options);
  }

  post(endpoint: string, data: object, options: RequestOption = {}) {
    return this.request('post', endpoint, data, options);
  }

  postFile(endpoint: string, file: File) {
    const data = new FormData();
    data.append('import', file);

    return this.post(endpoint, data);
  }

  put(endpoint: string, data: object, options: RequestOption = {}) {
    return this.request('put', endpoint, data, options);
  }

  async login(data: object) {
    const result = await this.post('/auth/jwt/create', data, { checkToken: false });
    const token: string | undefined = result.token || result.access;
    if (token) this.updateToken(token);
    return token;
  }

  async logout() {
    const result = await this.post('/auth/jwt/logout', {}, { checkToken: false });
    return result;
  }

  async refreshToken() {
    const { access } = await this.post('/auth/jwt/refresh', {}, { checkToken: false });
    this.updateToken(access);

    return access;
  }
}

const client = new Client(backendBaseUrl);

export default client;
